import GlobalStyles from '@mui/material/GlobalStyles';
import uiSettings from '../config/uiSettings';

const toCssSize = (value) => (typeof value === 'number' ? `${value}px` : value);

export const getThemeCssVariables = () => {
  const { colors, fontFamily, radius, spacing, shadows, layout } = uiSettings;

  return {
    '--saita-font-family': fontFamily,
    '--saita-color-primary': colors.primary,
    '--saita-color-primary-dark': colors.primaryDark,
    '--saita-color-primary-light': colors.primaryLight,
    '--saita-color-secondary': colors.secondary,
    '--saita-color-accent': colors.accent,
    '--saita-color-background': colors.background,
    '--saita-color-surface': colors.surface,
    '--saita-color-surface-muted': colors.surfaceMuted,
    '--saita-color-border': colors.border,
    '--saita-color-text-primary': colors.textPrimary,
    '--saita-color-text-secondary': colors.textSecondary,
    '--saita-color-text-muted': colors.textMuted,
    '--saita-color-success': colors.success,
    '--saita-color-warning': colors.warning,
    '--saita-color-error': colors.error,
    '--saita-color-info': colors.info,
    '--saita-color-primary-shadow': 'rgba(49, 92, 72, 0.16)',
    '--saita-color-success-soft': '#E8F3E9',
    '--saita-color-success-border': '#BBD9BD',
    '--saita-color-warning-soft': '#FCF0E0',
    '--saita-color-warning-border': '#F4D8B4',
    '--saita-color-error-soft': '#F8E7E7',
    '--saita-color-error-border': '#E8B9B9',
    '--saita-color-secondary-soft': '#F6EEE7',
    '--saita-color-overlay': 'rgba(255, 255, 255, 0.9)',
    '--saita-radius-xs': toCssSize(radius.xs),
    '--saita-radius-sm': toCssSize(radius.sm),
    '--saita-radius-md': toCssSize(radius.md),
    '--saita-radius-lg': toCssSize(radius.lg),
    '--saita-radius-xl': toCssSize(radius.xl),
    '--saita-page-x': spacing.pageX,
    '--saita-page-y': spacing.pageY,
    '--saita-section-gap': spacing.sectionGap,
    '--saita-panel-gap': spacing.panelGap,
    '--saita-shadow-sm': shadows.sm,
    '--saita-shadow-md': shadows.md,
    '--saita-shadow-lg': shadows.lg,
    '--saita-max-content-width': toCssSize(layout.maxContentWidth),
    '--saita-sidebar-width': toCssSize(layout.sidebarWidth),
    '--saita-header-height': toCssSize(layout.headerHeight),

    /* Legacy aliases used by older page styles. Keep these mapped here so
       page CSS can be themed without rewriting every selector at once. */
    '--font-primary': fontFamily,
    '--vazir': fontFamily,
    '--text-primary': colors.textPrimary,
    '--text-secondary': colors.textSecondary,
    '--text-muted': colors.textMuted,
    '--surface-1': colors.background,
    '--surface-2': colors.surfaceMuted,
    '--border': colors.border,
    '--border-strong': colors.border,
    '--color-primary': colors.primary,
    '--color-primary-dark': colors.primaryDark,
    '--color-primary-light': colors.primaryLight,
    '--color-secondary': colors.secondary,
    '--color-accent': colors.accent,
    '--color-background': colors.background,
    '--color-surface': colors.surface,
    '--color-border': colors.border,
    '--color-text': colors.textPrimary,
    '--color-text-secondary': colors.textSecondary,
    '--color-text-muted': colors.textMuted
  };
};

function GlobalTheme() {
  return (
    <GlobalStyles
      styles={{
        ':root': getThemeCssVariables(),
        'html, body, #root': {
          minHeight: '100%'
        },
        html: {
          direction: uiSettings.direction
        },
        '*, *::before, *::after': {
          boxSizing: 'border-box'
        },
        body: {
          margin: 0,
          fontFamily: 'var(--saita-font-family)',
          color: 'var(--saita-color-text-primary)',
          background: 'var(--saita-color-background)',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          lineHeight: 1.6
        },
        'button, input, textarea, select': {
          font: 'inherit'
        },
        'h1, h2, h3, h4, h5, h6, p': {
          marginTop: 0
        },
        a: {
          color: 'inherit'
        },
        '.MuiTypography-root, .MuiButton-root, .MuiInputBase-root, .MuiInputBase-input, .MuiInputLabel-root, .MuiFormLabel-root, .MuiFormHelperText-root, .MuiMenuItem-root, .MuiTableCell-root, .MuiChip-root, .MuiTab-root, .MuiAlert-root, .MuiDialog-root *, .MuiPopover-root *, .MuiMenu-root *, .MuiTooltip-popper *': {
          fontFamily: 'var(--saita-font-family)'
        },
        '.leaflet-control-layers': {
          background: 'var(--saita-color-surface)',
          border: '1px solid var(--saita-color-border)',
          borderRadius: 'var(--saita-radius-sm)',
          boxShadow: 'var(--saita-shadow-sm)'
        },
        '.leaflet-control-layers label:hover': {
          backgroundColor: 'var(--saita-color-surface-muted)'
        }
      }}
    />
  );
}

export default GlobalTheme;
